import { useCallback, useMemo } from "react";
import { customFieldTypes } from "constant";
import { getObjectsDifferences } from "utils/helpers";

const RUNTIME_LAYOUT_KEYS = [
  "i",
  "x",
  "y",
  "moved",
  "static",
  "isBounded",
  "isDraggable",
  "isResizable"
];

const stripRuntimeLayoutKeys = (item = {}) => {
  const layoutItem = { ...item };
  RUNTIME_LAYOUT_KEYS.forEach((key) => {
    delete layoutItem[key];
  });
  return layoutItem;
};

const cloneTileConfig = (item = {}) => ({
  x: item.x,
  y: item.y,
  w: item.w,
  h: item.h,
  minW: item.minW,
  maxW: item.maxW,
  minH: item.minH,
  maxH: item.maxH,
  resizeHandles: item.resizeHandles,
  static: item.static,
  isBounded: item.isBounded,
  isDraggable: item.isDraggable,
  isResizable: item.isResizable,
  i: item.i,
  moved: item.moved
});

const withLayoutDefaults = (item, index) => ({
  ...cloneTileConfig(item),
  i: String(index),
  static: false,
  isBounded: false,
  isDraggable: true,
  isResizable: Boolean(item.resizeHandles?.length),
  moved: false
});

const useGridTileSizes = ({ customFieldsList } = {}) => {
  const customRowsHeight = useMemo(() => {
    if (customFieldsList) {
      let rowsHeight = 1;
      customFieldsList.forEach((f) => {
        // eslint-disable-next-line eqeqeq
        if (f.type_id == customFieldTypes.find((cFT) => cFT.name === "Note").code) {
          rowsHeight += 2;
        } else {
          rowsHeight += 1;
        }
      });
      return rowsHeight;
    }
    return null;
  }, [customFieldsList]);

  const mergeViews = useCallback(
    (view, defaults) => {
      // If new user or new item added then reset the view look to defaults
      if (!Array.isArray(view) || defaults.length > view.length) {
        return defaults.map((item, index) => withLayoutDefaults(item, index));
      }

      return defaults.map((tileDefaultProps, index) => {
        if (view && view[index]) {
          const restTileDefaultProps = stripRuntimeLayoutKeys(tileDefaultProps);
          const restTileProps = stripRuntimeLayoutKeys(view[index]);

          const diffKeys = getObjectsDifferences(restTileProps, restTileDefaultProps).filter((keyName) =>
            Object.prototype.hasOwnProperty.call(tileDefaultProps, keyName)
          );

          const diffProps = diffKeys.reduce(
            (o, keyName) => ({
              ...o,
              [keyName]: tileDefaultProps[keyName]
            }),
            {}
          );

          return withLayoutDefaults(
            {
              ...cloneTileConfig(view[index]),
              ...diffProps
            },
            index
          );
        }
        return withLayoutDefaults(tileDefaultProps, index);
      });
    },
    [customFieldsList]
  );

  return {
    ...(customFieldsList && { customRowsHeight }),
    mergeViews
  };
};

export default useGridTileSizes;
